import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { Award, CheckCircle, XCircle, Clock, BookOpen, Calculator, ChevronDown, ChevronRight, Download, BarChart3 } from 'lucide-react';
import api from '../api';
import { convertCorrectAnswersToSAT } from '../utils/SATscoreCalculator';
import ImageViewer from '../components/ImageViewer';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { motion } from 'framer-motion';
import { ThemeContext } from '../ThemeContext';
import SATTestReportPDFLayout from './SATTestReportPDFLayout';

// Card component matching GameOptionsPage design
const Card = ({ children, className = '', delay = 0, index = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);
  const hoverColor = isDarkMode ? '#ff9e42' : '#06bae4';
  
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-3xl shadow-2xl p-6 border transition-all duration-300 ${className}`}
      style={{
        boxShadow: isHovered
          ? `0 20px 60px ${hexToRgba(hoverColor, 0.25)}, 0 0 40px ${hexToRgba(hoverColor, 0.2)}`
          : '0 10px 40px rgba(0, 0, 0, 0.1)',
        borderColor: isHovered 
          ? hexToRgba(hoverColor, 0.4)
          : isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
      }}
    >
      <motion.div
        className="absolute inset-0 rounded-3xl opacity-0 pointer-events-none"
        animate={{
          opacity: isHovered ? 0.2 : 0,
        }}
        transition={{ duration: 0.3 }}
        style={{
          background: `linear-gradient(135deg, ${hexToRgba(hoverColor, 0.25)}, ${hexToRgba(hoverColor, 0.4)})`,
          filter: 'blur(20px)',
          zIndex: -1,
        }}
      />
      {children}
    </motion.div>
  );
};

// Component for a single metric card
const MetricCard = ({ title, value, unit = '', icon: Icon, color = 'text-blue-600' }) => {
  const { isDarkMode } = useContext(ThemeContext);
  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md flex items-center space-x-4 transition-transform transform hover:scale-105">
      {Icon && <Icon size={32} style={{ color: '#a6a6a6' }} />}
      <div>
        <h3 className="text-gray-500 dark:text-gray-400 text-sm font-semibold">{title}</h3>
        <p className="text-2xl font-bold text-gray-800 dark:text-gray-200">{value}{unit}</p>
      </div>
    </div>
  );
};

// Progress Circle for topic breakdown
// const ProgressCircle = ({ correct, incorrect, title, size = 90, strokeWidth = 10 }) => {
//   const [isHovered, setIsHovered] = useState(false);
//   const total = correct + incorrect ;

//   const radius = (size - strokeWidth) / 2;
//   const circumference = 2 * Math.PI * radius;
//   //   if (total === 0) {
//   //     return (
//   //         <div
//   //         className="relative flex flex-col items-center justify-center p-2 bg-white rounded-xl shadow-sm"
//   //         onMouseEnter={() => setIsHovered(true)}
//   //         onMouseLeave={() => setIsHovered(false)}
//   //       >
//   //         <p className="text-sm font-semibold text-gray-700 mb-1">{title}</p>

//   //         {/* Accuracy percentage and "Accuracy" text in the center with larger font size and centered */}
//   //         <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
//   //           <span className="text-xl font-bold text-gray-800 mt-4">N/A</span>
//   //         </div>
//   //       </div>
//   //     );
//   //   }
//   const correctRatio = correct / total;
//   const correctDash = circumference * correctRatio;
//   const accuracy = (correctRatio * 100).toFixed(0);

//   return (
//     <div
//       className="relative flex flex-col items-center justify-center p-2 bg-white rounded-xl shadow-sm"
//       onMouseEnter={() => setIsHovered(true)}
//       onMouseLeave={() => setIsHovered(false)}
//     >
//       <p className="text-sm font-semibold text-gray-700 mb-1">{title}</p>

//       <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
//         {/* Background circle for incorrect portion */}
//         <circle
//           stroke="#ef4444" // Red for incorrect
//           strokeWidth={strokeWidth}
//           fill="transparent"
//           r={radius}
//           cx={size / 2}
//           cy={size / 2}
//         />
//         {/* Foreground circle for correct portion */}
//         <circle
//           stroke="#22c55e" // Green for correct
//           strokeWidth={strokeWidth}
//           fill="transparent"
//           r={radius}
//           cx={size / 2}
//           cy={size / 2}
//           strokeDasharray={`${correctDash} ${circumference - correctDash}`}
//           strokeDashoffset={circumference - correctDash} // Start from the top
//           strokeLinecap="round"
//           transform={`rotate(-90 ${size / 2} ${size / 2})`} // Start from top
//         />
//       </svg>
//       {/* Accuracy percentage and "Accuracy" text in the center with larger font size and centered */}
//       <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
//         {correct+incorrect>0 ? <span className="text-xl font-bold text-gray-800 mt-4">{accuracy}%</span> : <span className="text-xl font-bold text-gray-800 mt-4">No</span>}  
//         {correct+incorrect>0 ? <span className="text-xs text-gray-600">Accuracy</span> : <span className="text-xs text-gray-600">Data</span>}
//       </div>
//       {isHovered && (
//         <div className="absolute top-full mt-2 p-2 bg-gray-800 text-white text-xs rounded-md shadow-lg whitespace-nowrap z-20">
//           Correct: {correct}, Incorrect: {incorrect}
//         </div>
//       )}
//     </div>
//   );
// };

const QUESTION_TABS = [
  { key: 'english1', label: 'English Module 1' },
  { key: 'english2', label: 'English Module 2' },
  { key: 'math1', label: 'Math Module 1' },
  { key: 'math2', label: 'Math Module 2' },
];

// const PLACEHOLDER_QUESTIONS = Array.from({ length: 8 }).map((_, i) => ({
//   id: i + 1,
//   status: ['Correct', 'Incorrect', 'Unanswered'][i % 3],
//   content: `Placeholder Question ${i + 1}`,
//   type: ['MCQ', 'Graph', 'Grid In'][i % 3],
//   difficulty: ['Easy', 'Medium', 'Hard'][i % 3],
// }));

const QuestionTable = ({ questions, loading, error, printMode = false }) => {
  const { isDarkMode } = useContext(ThemeContext);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState('All');
  const filteredQuestions = filter === 'All' ? questions : questions.filter(q => {
    const status = (q.question_status || q.status || '').toLowerCase();
    return status === filter.toLowerCase();
  });

  // Helper functions for result styling (similar to AnswerDetailPage)
  const getResultHeader = (status) => {
    switch (status) {
      case 'correct': return "CORRECT!";
      case 'incorrect': return "INCORRECT";
      case 'unanswered': return "UNANSWERED";
      default: return "Result";
    }
  }
  
  const getResultBorderColor = (status) => {
    switch (status) {
      case 'correct': return 'border-green-500 dark:border-green-400 bg-green-50 dark:bg-green-900/20';
      case 'incorrect': return 'border-red-500 dark:border-red-400 bg-red-50 dark:bg-red-900/20';
      case 'unanswered': return 'border-yellow-500 dark:border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20';
      default: return 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800';
    }
  }
  
  const getResultTextColor = (status) => {
    switch (status) {
      case 'correct': return 'text-green-700 dark:text-green-300';
      case 'incorrect': return 'text-red-700 dark:text-red-300';
      case 'unanswered': return 'text-yellow-700 dark:text-yellow-300';
      default: return 'text-gray-700 dark:text-gray-300';
    }
  }

  // Helper function to get answer option keys
  const getAnswerOptionKeys = (question) => {
    const answerOptionKeys = ['A', 'B', 'C', 'D'];
    return answerOptionKeys.map(key => {
      const value = question['question_option_' + key.toLowerCase()];
      return value ? { key, value } : null;
    }).filter(opt => opt !== null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[120px] text-gray-500 dark:text-gray-400">
        <svg className="animate-spin h-6 w-6 mr-2 dark:text-green-400" style={{ color: '#34c759' }} viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
        Loading your questions...
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[120px] text-red-500 dark:text-red-400 font-semibold">Error fetching questions.</div>
    );
  }
  const renderTextWithPi = (text) => {
    if (!text.includes('π')) return text;
    
    return text.split('π').map((part, index) => (
      <React.Fragment key={index}>
        {part}
        {index < text.split('π').length - 1 && (
          <span className="font-sans">π</span>
        )}
      </React.Fragment>
    ));
  };
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 mt-4 ${printMode ? '' : 'max-h-100 overflow-y-auto'}`}>
      {!printMode && (
        <div className="flex justify-end mb-2">
          <label className="mr-2 font-semibold text-gray-600 dark:text-gray-400">Filter:</label>
          <select
            className="border rounded px-2 py-1 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
            value={filter}
            onChange={e => setFilter(e.target.value)}
          >
            <option value="All">All</option>
            <option value="Correct">Correct</option>
            <option value="Incorrect">Incorrect</option>
            <option value="Unanswered">Unanswered</option>
          </select>
        </div>
      )}
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" style={{ height: 'auto' }}>
        <thead>
          <tr>
            <th className="px-2 py-2 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">#</th>
            <th className="px-2 py-2"></th>
            <th className="px-2 py-2 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Status</th>
            <th className="px-2 py-2 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Subject</th>
            <th className="px-2 py-2 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Type</th>
            <th className="px-2 py-2 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Difficulty</th>
            <th className="px-2 py-2"></th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
          {filteredQuestions.length === 0 ? (
            <tr><td colSpan={7} className="text-center text-gray-400 dark:text-gray-500 py-4">No questions found.</td></tr>
          ) : filteredQuestions.map((q, idx) => (
            <React.Fragment key={q.question_id || q.id}>
              <motion.tr
                className={printMode ? "bg-white dark:bg-gray-800" : "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"}
                onClick={() => { if (!printMode) setExpanded(expanded === (q.question_id || q.id) ? null : (q.question_id || q.id)) }}
                initial={printMode ? false : { opacity: 0, y: 20 }}
                animate={printMode ? false : { opacity: 1, y: 0 }}
                transition={printMode ? {} : { delay: idx * 0.05 }}
              >
                <td className="px-2 py-2 text-gray-500 dark:text-gray-400 font-semibold">{idx + 1}</td>
                <td className="px-2 py-2">
                  {!printMode && (expanded === (q.question_id || q.id) ? <ChevronDown size={18} className="text-gray-600 dark:text-gray-400" /> : <ChevronRight size={18} className="text-gray-600 dark:text-gray-400" />)}
                </td>
                <td className="px-2 py-2">
                  <span className={
                    (q.question_status || q.status || '').toLowerCase() === 'correct' ? 'text-green-600 dark:text-green-400 font-semibold' :
                      (q.question_status || q.status || '').toLowerCase() === 'incorrect' ? 'text-red-500 dark:text-red-400 font-semibold' :
                        'text-yellow-400 dark:text-yellow-500 font-semibold'
                  }>{q.question_status || q.status}</span>
                </td>
                <td className="px-2 py-2 text-gray-800 dark:text-gray-200">{q.question_content=='Sentence Completion' ? 'Vocablary In Context' : q.question_content}</td>
                <td className="px-2 py-2 text-gray-800 dark:text-gray-200">{q.question_type || q.type}</td>
                <td className="px-2 py-2 text-gray-800 dark:text-gray-200">{q.question_difficulty || q.difficulty}</td>
                <td className="px-2 py-2 text-right"></td>
              </motion.tr>
              <tr>
                <td colSpan={7} className="bg-gray-50 dark:bg-gray-700/50 px-4 border-t border-gray-200 dark:border-gray-600 transition-all duration-500 ease-in-out">
                  <div
                    className={`transition-all duration-1000 ease-in-out ${printMode || expanded === (q.question_id || q.id) ? 'max-h-[4000px] py-6 opacity-100' : 'max-h-0 opacity-0 py-0 overflow-hidden'
                      }`}
                  >
                    <div className="md:flex md:gap-8">
                      {/* Left Side: Question & Feedback */}
                      <div className="md:w-3/5 lg:w-1/2 space-y-5">
                        <h1 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-gray-200 font-zonaProUltra mb-1">Question:</h1>
                        {q.image_name && (
                          <div className="my-4 rounded-lg overflow-hidden shadow max-h-60 flex justify-center items-center bg-gray-50 dark:bg-gray-800">
                            <ImageViewer fileName={q.image_name} alt="Question image" className="max-h-full max-w-full object-contain" />
                          </div>
                        )}
                        <p className="text-base md:text-lg text-gray-700 dark:text-gray-300 text-left leading-relaxed whitespace-pre-line">
                          {renderTextWithPi(q.question_text || q.question_choice || 'N/A')}
                        </p>
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`mt-4 p-4 rounded-lg border-l-4 ${getResultBorderColor(q.question_status)}`}
                        >
                          <h3 className={`text-lg font-semibold mb-2 font-zonaProUltra ${getResultTextColor(q.question_status)}`}>
                            {getResultHeader(q.question_status)}
                          </h3>
                          <p className={`mb-2 ${getResultTextColor(q.question_status)}`}>{q.question_feedback || 'N/A'}</p>
                          {q.question_status !== 'unanswered' && q.points !== undefined && (
                            <div className={`text-base font-semibold ${getResultTextColor(q.question_status)}`}>
                              Points: <span className="font-bold">{q.points > 0 ? `+${q.points}` : q.points}</span>
                            </div>
                          )}
                        </motion.div>
                      </div>

                      {/* Right Side: Answer Options */}
                      <div className="md:w-2/5 lg:w-1/2 md:pl-4 lg:pl-6 mt-6 md:mt-0">
                        <h2 className="text-lg sm:text-xl font-zonaProUltra text-gray-700 dark:text-gray-300 mb-4">
                          Results:
                        </h2>
                        {q.question_type === 'Grid-in' ? (
                          <div className="space-y-3">
                            {q.question_status === 'correct' && (
                              <div className="w-full text-left p-3 rounded-lg border-2 font-medium bg-green-50 dark:bg-green-900/30 border-green-500 dark:border-green-600 text-green-700 dark:text-green-300">
                                <span className="font-bold mr-2">Your Answer:</span>{renderTextWithPi(q.question_selected_option || 'N/A')}
                              </div>
                            )}

                            {q.question_status === 'incorrect' && (
                              <>
                                <div className="w-full text-left p-3 rounded-lg border-2 font-medium bg-red-50 dark:bg-red-900/30 border-red-500 dark:border-red-600 text-red-700 dark:text-red-300">
                                  <span className="font-bold mr-2">Your Answer:</span>{renderTextWithPi(q.question_selected_option || 'N/A')}
                                </div>
                                <div className="w-full text-left p-3 rounded-lg border-2 font-medium bg-green-50 dark:bg-green-900/30 border-green-500 dark:border-green-600 text-green-700 dark:text-green-300">
                                  <span className="font-bold mr-2">Correct Answer:</span>{renderTextWithPi(q.question_answer || 'N/A')}
                                </div>
                              </>
                            )}

                            {q.question_status === 'unanswered' && (
                              <div className="w-full text-left p-3 rounded-lg border-2 font-medium bg-green-50 dark:bg-green-900/30 border-green-500 dark:border-green-600 text-green-700 dark:text-green-300">
                                <span className="font-bold mr-2">Correct Answer:</span>{renderTextWithPi(q.question_answer || 'N/A')}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {getAnswerOptionKeys(q).map((opt) => {
                              const isUserAnswer = q.question_selected_option === opt.value;
                              const isCorrectAnswer = q.question_answer === opt.value;

                              let classes = "w-full text-left p-3 rounded-lg border-2 font-medium";

                              if (isUserAnswer && !isCorrectAnswer) {
                                classes += ' bg-red-50 dark:bg-red-900/30 border-red-400 dark:border-red-600 text-red-700 dark:text-red-300'; // User's incorrect choice
                              } else if (isCorrectAnswer) {
                                classes += ' bg-green-50 dark:bg-green-900/30 border-green-500 dark:border-green-600 text-green-700 dark:text-green-300'; // The correct answer
                              } else {
                                classes += ' bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 opacity-75'; // Other options
                              }

                              return (
                                <div key={opt.key} className={classes}>
                                  <span className="font-bold mr-2">{opt.key})</span>{renderTextWithPi(opt.value)}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
              </tr>

            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Utility to format seconds as hr/min/sec
function formatDuration(seconds) {
  if (seconds == null) return 'N/A';
  seconds = Math.round(seconds);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  let result = '';
  if (h > 0) result += `${h} hr${h > 1 ? 's' : ''} `;
  if (m > 0) result += `${m} min${m > 1 ? 's' : ''} `;
  // Only show seconds if there are no hours and no minutes
  if (h === 0 && m === 0) result += `${s} sec${s !== 1 ? 's' : ''}`;
  return result.trim();
}

const SATTestReportPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode } = useContext(ThemeContext);
  const reportContentRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [barWidths, setBarWidths] = useState({ correct: 0, incorrect: 0, unanswered: 0 });
  const [selectedTab, setSelectedTab] = useState('english1');
  const [questionsByTab, setQuestionsByTab] = useState({
    english1: [],
    english2: [],
    math1: [],
    math2: [],
  });
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [questionsError, setQuestionsError] = useState(false);

  // Get testId from state or localStorage
  console.log(location.state);
  const testId = location.state?.reportId || localStorage.getItem('sat_test_report_id');
  const fromSatTest = location.state?.fromSatTest || false;
  const topicTotalCount={math:44,english:54,algebra:8,advanced_math:7,problem_solving_and_data_analysis:3,geometry_and_trigonometry:4,reading:9,writing:13,sentence_completion:5}

  // PDF generation function
  const generatePdf = async () => {
    const container = document.getElementById('pdf-layout-container');
    const actionButtons = document.getElementById('action-buttons');

    if (!container) {
      alert("Report elements not found for PDF generation.");
      return;
    }

    if (actionButtons) {
      actionButtons.classList.add('hidden');
    }

    try {
      const images = container.getElementsByTagName('img');
      await Promise.all(Array.from(images).map(img => img.complete ? Promise.resolve() : new Promise(res => { img.onload = res; img.onerror = res; })));

      const canvas = await html2canvas(container, { 
        scale: 2,
        useCORS: true 
      });

      if (actionButtons) {
        actionButtons.classList.remove('hidden');
      }

      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgProps = pdf.getImageProperties(imgData);
      const imgWidth = pageWidth;
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

      let heightLeft = imgHeight;
      let position = 0;
      
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft > 0) {
        position = position - pageHeight; 
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Get test date from reportData or mappedData
      let testDateStr = 'test';
      if (reportData?.created_date) {
        testDateStr = new Date(reportData.created_date).toLocaleDateString().replace(/\//g, '-');
      } else if (mappedData?.testDate) {
        testDateStr = mappedData.testDate.replace(/\//g, '-');
      }
      pdf.save(`SAT_Report_${testDateStr}.pdf`);
    } catch (e) {
      console.error("Error generating PDF:", e);
      alert("Could not generate PDF. Please try again. Check the console for details.");
      if (actionButtons) {
        actionButtons.classList.remove('hidden');
      }
    }
  };

  // Generate base64 PDF for emailing (reduced size for email attachment)
  const generatePracticePdfBase64 = async () => {
    const container = document.getElementById('pdf-layout-container');

    if (!container) {
      throw new Error('Report elements not found for PDF generation.');
    }

    const images = container.getElementsByTagName('img');
    await Promise.all(Array.from(images).map(img => img.complete ? Promise.resolve() : new Promise(res => { img.onload = res; img.onerror = res; })));

    // Reduced scale and quality for smaller email attachment size
    const canvas = await html2canvas(container, { 
      scale: 1.2, // Reduced from 2 to 1.2 for smaller file size
      useCORS: true,
      logging: false,
    });

    // Reduced JPEG quality from 1.0 to 0.75 for smaller file size
    const imgData = canvas.toDataURL('image/jpeg', 0.75);
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgProps = pdf.getImageProperties(imgData);
    const imgWidth = pageWidth;
    const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

    let heightLeft = imgHeight;
    let position = 0;
    
    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    
    while (heightLeft > 0) {
      position = position - pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    const pdfDataUri = pdf.output('datauristring');
    const base64 = pdfDataUri.includes(',') ? pdfDataUri.split(',')[1] : '';
    return base64;
  };

  // Email report function (manual trigger)
  const handleEmailReport = async () => {
    try {
      const sat_user = JSON.parse(localStorage.getItem('sat_user'));
      const userId = sat_user?.user_id;
      if (!userId || !testId || !reportData) {
        alert('Unable to send email. Please try again.');
        return;
      }
      
      const pdfBase64 = await generatePracticePdfBase64();
      await api.post('/api/practice/send_practice_report_email/', {
        test_id: testId,
        pdf_base64: pdfBase64,
      });
      alert('Email sent! Please check your inbox.');
    } catch (e) {
      console.error('Failed to email practice report', e);
      const msg = e?.response?.data?.error || e?.message || 'Could not send email. Please try again.';
      alert(msg);
    }
  };
  useEffect(() => {
    if (!testId) {
      navigate('/practice');
      return;
    }
    localStorage.setItem('sat_test_report_id', testId);
    setLoading(true);
    setError(null);
    console.log('testId', testId);
    api.get(`/api/practice/get_report/${testId}`)
      .then(res => {
        setReportData(res.data.report);
        setLoading(false);
      })
      .catch(err => {
        setError('Failed to load report. Please try again.');
        setLoading(false);
      });
    // Fetch question history
    setQuestionsLoading(true);
    setQuestionsError(false);
    const sat_user = JSON.parse(localStorage.getItem('sat_user'));
    const userId = sat_user.user_id;
    api.get(`/api/practice/get_practice_question_history/${testId}`)
      .then(res => {
        const questions = res.data.questions_history || [];
        // Group questions by category and module
        const grouped = { english1: [], english2: [], math1: [], math2: [] };
        questions.forEach(q => {
          const cat = (q.question_category || '').toLowerCase();
          const mod = String(q.question_module);
          if (cat === 'english') {
            if (mod === '1') grouped.english1.push(q);
            else if (mod === '2') grouped.english2.push(q);
          } else if (cat === 'math' || cat === 'math') {
            if (mod === '1') grouped.math1.push(q);
            else if (mod === '2') grouped.math2.push(q);
          }
        });
        setQuestionsByTab(grouped);
        setQuestionsLoading(false);
      })
      .catch(() => {
        setQuestionsError(true);
        setQuestionsLoading(false);
      });
  }, [testId, navigate]);

  // Map real API data to the structure used in the old mock code
  let mappedData = null;
  if (reportData) {
    // English modules
    const englishModules = [
      {
        name: 'English Module 1',
        timeLimit: 32,
        timeSpent: reportData.english['module-1'].time_spent || 0,
        totalQuestions: topicTotalCount.english/2,
        correct: reportData.english['module-1'].correct,
        incorrect: reportData.english['module-1'].incorrect,
        unanswered: reportData.english['module-1'].unanswered,
        breakdown: [
          { type: 'Reading', total: 0, correct: reportData.reading['module-1'].correct, incorrect: reportData.reading['module-1'].incorrect, unanswered: topicTotalCount.reading-reportData.reading['module-1'].correct-reportData.reading['module-1'].incorrect, accuracy: reportData.reading['module-1'].correct + reportData.reading['module-1'].incorrect > 0 ? Math.round((reportData.reading['module-1'].correct / (9)) * 100) : 0 },
          { type: 'Writing', total: 0, correct: reportData.writing['module-1'].correct, incorrect: reportData.writing['module-1'].incorrect, unanswered: topicTotalCount.writing-reportData.writing['module-1'].correct-reportData.writing['module-1'].incorrect, accuracy: reportData.writing['module-1'].correct + reportData.writing['module-1'].incorrect > 0 ? Math.round((reportData.writing['module-1'].correct / (13)) * 100) : 0 },
          { type: 'Vocablary In Context', total: 0, correct: reportData.sentence_completion['module-1'].correct, incorrect: reportData.sentence_completion['module-1'].incorrect, unanswered: topicTotalCount.sentence_completion-reportData.sentence_completion['module-1'].correct-reportData.sentence_completion['module-1'].incorrect, accuracy: reportData.sentence_completion['module-1'].correct + reportData.sentence_completion['module-1'].incorrect > 0 ? Math.round((reportData.sentence_completion['module-1'].correct / (5)) * 100) : 0 },
        ]
      },
      {
        name: 'English Module 2',
        timeLimit: 32,
        timeSpent: reportData.english['module-2'].time_spent || 0,
        totalQuestions: topicTotalCount.english/2,
        correct: reportData.english['module-2'].correct,
        incorrect: reportData.english['module-2'].incorrect,
        unanswered: reportData.english['module-2'].unanswered,
        breakdown: [
          { type: 'Reading', total: 0, correct: reportData.reading['module-2'].correct, incorrect: reportData.reading['module-2'].incorrect, unanswered: topicTotalCount.reading-reportData.reading['module-2'].correct-reportData.reading['module-2'].incorrect, accuracy: reportData.reading['module-2'].correct + reportData.reading['module-2'].incorrect > 0 ? Math.round((reportData.reading['module-2'].correct / (9)) * 100) : 0 },
          { type: 'Writing', total: 0, correct: reportData.writing['module-2'].correct, incorrect: reportData.writing['module-2'].incorrect, unanswered: topicTotalCount.writing-reportData.writing['module-2'].correct-reportData.writing['module-2'].incorrect, accuracy: reportData.writing['module-2'].correct + reportData.writing['module-2'].incorrect > 0 ? Math.round((reportData.writing['module-2'].correct / (13)) * 100) : 0 },
          { type: 'Vocablary In Context', total: 0, correct: reportData.sentence_completion['module-2'].correct, incorrect: reportData.sentence_completion['module-2'].incorrect, unanswered: topicTotalCount.sentence_completion-reportData.sentence_completion['module-2'].correct-reportData.sentence_completion['module-2'].incorrect, accuracy: reportData.sentence_completion['module-2'].correct + reportData.sentence_completion['module-2'].incorrect > 0 ? Math.round((reportData.sentence_completion['module-2'].correct / (5)) * 100) : 0 },
        ]
      }
    ];
    // Math modules
    const mathModules = [
      {
        name: 'Math Module 1',
        timeLimit: 35,
        timeSpent: reportData.math['module-1'].time_spent || 0,
        totalQuestions: topicTotalCount.math/2,
        correct: reportData.math['module-1'].correct,
        incorrect: reportData.math['module-1'].incorrect,
        unanswered: reportData.math['module-1'].unanswered,
        breakdown: [
          { subTopic: 'Algebra', total: 0, correct: reportData.algebra['module-1'].correct, incorrect: reportData.algebra['module-1'].incorrect, unanswered: topicTotalCount.algebra-reportData.algebra['module-1'].correct-reportData.algebra['module-1'].incorrect, accuracy: reportData.algebra['module-1'].correct + reportData.algebra['module-1'].incorrect > 0 ? Math.round((reportData.algebra['module-1'].correct / (8)) * 100) : 0 },
          { subTopic: 'Advanced Math', total: 0, correct: reportData.advanced_math['module-1'].correct, incorrect: reportData.advanced_math['module-1'].incorrect, unanswered: topicTotalCount.advanced_math-reportData.advanced_math['module-1'].correct-reportData.advanced_math['module-1'].incorrect, accuracy: reportData.advanced_math['module-1'].correct + reportData.advanced_math['module-1'].incorrect > 0 ? Math.round((reportData.advanced_math['module-1'].correct / (7)) * 100) : 0 },
          { subTopic: 'Problem Solving & Data Analysis', total: 0, correct: reportData.problem_solving_and_data_analysis['module-1'].correct, incorrect: reportData.problem_solving_and_data_analysis['module-1'].incorrect, unanswered: topicTotalCount.problem_solving_and_data_analysis-reportData.problem_solving_and_data_analysis['module-1'].correct-reportData.problem_solving_and_data_analysis['module-1'].incorrect, accuracy: reportData.problem_solving_and_data_analysis['module-1'].correct + reportData.problem_solving_and_data_analysis['module-1'].incorrect > 0 ? Math.round((reportData.problem_solving_and_data_analysis['module-1'].correct / (3)) * 100) : 0 },
          { subTopic: 'Geometry & Trignometry', total: 0, correct: reportData.geometry_and_trigonometry['module-1'].correct, incorrect: reportData.geometry_and_trigonometry['module-1'].incorrect, unanswered: topicTotalCount.geometry_and_trigonometry-reportData.geometry_and_trigonometry['module-1'].correct-reportData.geometry_and_trigonometry['module-1'].incorrect, accuracy: reportData.geometry_and_trigonometry['module-1'].correct + reportData.geometry_and_trigonometry['module-1'].incorrect > 0 ? Math.round((reportData.geometry_and_trigonometry['module-1'].correct / (4)) * 100) : 0 },
        ]
      },
      {
        name: 'Math Module 2',
        timeLimit: 35,
        timeSpent: reportData.math['module-2'].time_spent || 0,
        totalQuestions: topicTotalCount.math/2,
        correct: reportData.math['module-2'].correct,
        incorrect: reportData.math['module-2'].incorrect,
        unanswered: reportData.math['module-2'].unanswered,
        breakdown: [
          { subTopic: 'Algebra', total: 0, correct: reportData.algebra['module-2'].correct, incorrect: reportData.algebra['module-2'].incorrect, unanswered: topicTotalCount.algebra-reportData.algebra['module-2'].correct-reportData.algebra['module-2'].incorrect, accuracy: reportData.algebra['module-2'].correct + reportData.algebra['module-2'].incorrect > 0 ? Math.round((reportData.algebra['module-2'].correct / (8)) * 100) : 0 },
          { subTopic: 'Advanced Math', total: 0, correct: reportData.advanced_math['module-2'].correct, incorrect: reportData.advanced_math['module-2'].incorrect, unanswered: topicTotalCount.advanced_math-reportData.advanced_math['module-2'].correct-reportData.advanced_math['module-2'].incorrect, accuracy: reportData.advanced_math['module-2'].correct + reportData.advanced_math['module-2'].incorrect > 0 ? Math.round((reportData.advanced_math['module-2'].correct / (7)) * 100) : 0 },
          { subTopic: 'Problem Solving & Data Analysis', total: 0, correct: reportData.problem_solving_and_data_analysis['module-2'].correct, incorrect: reportData.problem_solving_and_data_analysis['module-2'].incorrect, unanswered: topicTotalCount.problem_solving_and_data_analysis-reportData.problem_solving_and_data_analysis['module-2'].correct-reportData.problem_solving_and_data_analysis['module-2'].incorrect, accuracy: reportData.problem_solving_and_data_analysis['module-2'].correct + reportData.problem_solving_and_data_analysis['module-2'].incorrect > 0 ? Math.round((reportData.problem_solving_and_data_analysis['module-2'].correct / (3)) * 100) : 0 },
          { subTopic: 'Geometry & Trignometry', total: 0, correct: reportData.geometry_and_trigonometry['module-2'].correct, incorrect: reportData.geometry_and_trigonometry['module-2'].incorrect, unanswered: topicTotalCount.geometry_and_trigonometry-reportData.geometry_and_trigonometry['module-2'].correct-reportData.geometry_and_trigonometry['module-2'].incorrect, accuracy: reportData.geometry_and_trigonometry['module-2'].correct + reportData.geometry_and_trigonometry['module-2'].incorrect > 0 ? Math.round((reportData.geometry_and_trigonometry['module-2'].correct / (4)) * 100) : 0 },
        ]
      }
    ];
    // English section overall breakdown
    const englishSectionOverallBreakdownData = [
      { type: 'Reading', correct: reportData.reading['module-1'].correct + reportData.reading['module-2'].correct, incorrect: reportData.reading['module-1'].incorrect + reportData.reading['module-2'].incorrect },
      { type: 'Writing', correct: reportData.writing['module-1'].correct + reportData.writing['module-2'].correct, incorrect: reportData.writing['module-1'].incorrect + reportData.writing['module-2'].incorrect },
      { type: 'Vocablary In Context', correct: reportData.sentence_completion['module-1'].correct + reportData.sentence_completion['module-2'].correct, incorrect: reportData.sentence_completion['module-1'].incorrect + reportData.sentence_completion['module-2'].incorrect },
    ];
    // Math section overall breakdown
    const mathSectionOverallBreakdownData = [
      { subTopic: 'Algebra', correct: reportData.algebra['module-1'].correct + reportData.algebra['module-2'].correct, incorrect: reportData.algebra['module-1'].incorrect + reportData.algebra['module-2'].incorrect },
      { subTopic: 'Advanced Math', correct: reportData.advanced_math['module-1'].correct + reportData.advanced_math['module-2'].correct, incorrect: reportData.advanced_math['module-1'].incorrect + reportData.advanced_math['module-2'].incorrect },
      { subTopic: 'Problem Solving & Data Analysis', correct: reportData.problem_solving_and_data_analysis['module-1'].correct + reportData.problem_solving_and_data_analysis['module-2'].correct, incorrect: reportData.problem_solving_and_data_analysis['module-1'].incorrect + reportData.problem_solving_and_data_analysis['module-2'].incorrect },
      { subTopic: 'Geometry & Trig', correct: reportData.geometry_and_trigonometry['module-1'].correct + reportData.geometry_and_trigonometry['module-2'].correct, incorrect: reportData.geometry_and_trigonometry['module-1'].incorrect + reportData.geometry_and_trigonometry['module-2'].incorrect },
    ];
    // Section scores
    const mathScore = convertCorrectAnswersToSAT(reportData.math['module-1'].correct + reportData.math['module-2'].correct, 'math');
    const englishScore = convertCorrectAnswersToSAT(reportData.english['module-1'].correct + reportData.english['module-2'].correct, 'english');
    const totalScore = mathScore + englishScore;
    // Totals
    const overallCorrect = reportData.english['module-1'].correct + reportData.english['module-2'].correct + reportData.math['module-1'].correct + reportData.math['module-2'].correct;
    const overallIncorrect = reportData.english['module-1'].incorrect + reportData.english['module-2'].incorrect + reportData.math['module-1'].incorrect + reportData.math['module-2'].incorrect;
    const overallunanswered = reportData.english['module-1'].unanswered + reportData.english['module-2'].unanswered + reportData.math['module-1'].unanswered + reportData.math['module-2'].unanswered;
    const overallQuestions = 98;
    const overallAccuracyPercentage = overallQuestions > 0 ? ((overallCorrect / overallQuestions) * 100).toFixed(1) : 0;
    const totalEnglishTime = (reportData.english['module-1'].time_spent || 0) + (reportData.english['module-2'].time_spent || 0);
    const totalMathTime = (reportData.math['module-1'].time_spent || 0) + (reportData.math['module-2'].time_spent || 0);
    const totalTimeTakenSeconds = reportData.time_taken;
    const totalTimeTakenDisplay = formatDuration(totalTimeTakenSeconds);
    mappedData = {
      overallScore: totalScore,
      maxScore: 1600,
      testDate: reportData.created_date ? new Date(reportData.created_date).toLocaleDateString() : '',
      englishSection: {
        name: 'English Section',
        score: englishScore,
        maxScore: 800,
        totalQuestions: reportData.english['module-1'].correct + reportData.english['module-1'].incorrect + reportData.english['module-1'].unanswered + reportData.english['module-2'].correct + reportData.english['module-2'].incorrect + reportData.english['module-2'].unanswered,
        correct: reportData.english['module-1'].correct + reportData.english['module-2'].correct,
        incorrect: reportData.english['module-1'].incorrect + reportData.english['module-2'].incorrect,
        unanswered: reportData.english['module-1'].unanswered + reportData.english['module-2'].unanswered,
        modules: englishModules
      },
      mathSection: {
        name: 'Math Section',
        score: mathScore,
        maxScore: 800,
        totalQuestions: reportData.math['module-1'].correct + reportData.math['module-1'].incorrect + reportData.math['module-1'].unanswered + reportData.math['module-2'].correct + reportData.math['module-2'].incorrect + reportData.math['module-2'].unanswered,
        correct: reportData.math['module-1'].correct + reportData.math['module-2'].correct,
        incorrect: reportData.math['module-1'].incorrect + reportData.math['module-2'].incorrect,
        unanswered: reportData.math['module-1'].unanswered + reportData.math['module-2'].unanswered,
        modules: mathModules
      },
      englishSectionOverallBreakdownData,
      mathSectionOverallBreakdownData,
      overallCorrect,
      overallIncorrect,
      overallunanswered,
      overallQuestions,
      overallAccuracyPercentage,
      totalEnglishTime,
      totalMathTime,
      totalTimeTakenSeconds,
      totalTimeTakenDisplay
    };
  }

  useEffect(() => {
    if (mappedData && mappedData.overallQuestions > 0) {
      setBarWidths({
        correct: (mappedData.overallCorrect / (topicTotalCount.english+topicTotalCount.math)) * 100,
        incorrect: (mappedData.overallIncorrect / (topicTotalCount.english+topicTotalCount.math)) * 100,
        unanswered: (topicTotalCount.english+topicTotalCount.math-mappedData.overallCorrect-mappedData.overallIncorrect) / (topicTotalCount.english+topicTotalCount.math) * 100,
      });
    }
  }, [reportData]);

  // Auto-send email with PDF once report and questions are loaded (only when coming from SatTestPage)
  const emailSentRef = useRef(false);
  useEffect(() => {
    const sendEmail = async () => {
      try {
        const sat_user = JSON.parse(localStorage.getItem('sat_user'));
        const userId = sat_user?.user_id;
        if (!userId || !testId || !reportData) return;
        const pdfBase64 = await generatePracticePdfBase64();
        await api.post('/api/practice/send_practice_report_email/', {
          test_id: testId,
          pdf_base64: pdfBase64,
        });
      } catch (e) {
        console.error('Failed to email practice report', e);
      }
    };
    // Only auto-email if user came from SatTestPage (fromSatTest flag is true)
    if (fromSatTest && !emailSentRef.current && !loading && !questionsLoading && reportData) {
      emailSentRef.current = true;
      sendEmail();
    }
  }, [loading, questionsLoading, reportData, testId, fromSatTest]);

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 font-zonaProRegular">
        <Card delay={0} index={0} className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 dark:border-orange-500 mx-auto mb-4"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400 font-zonaProRegular">Loading your test report...</p>
        </Card>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 font-zonaProRegular">
        <Card delay={0} index={0} className="text-center">
          <p className="text-red-500 dark:text-red-400 mb-4">{error}</p>
          <motion.button
            onClick={() => navigate('/practice')}
            className="px-4 py-2 bg-[#34c759] text-white rounded-lg hover:shadow-lg hover:shadow-[#34c759]/50 transition-all"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Return to Practice
          </motion.button>
        </Card>
      </div>
    );
  }
  if (!mappedData) return null;

  return (
    <div className="min-h-screen w-full flex flex-col items-center relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 font-zonaProRegular">
      <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6 w-full max-w-7xl mx-auto">
        {/* Report Content Container (PDF Reference) */}
        <div ref={reportContentRef} id="main-report-container">
          <div id="main-report-content">
            <h1
          key={`title-${isDarkMode}`}
          className="text-2xl sm:text-4xl lg:text-5xl font-bold font-zonaProUltra mb-6 sm:mb-10 text-center relative inline-block"
          style={{
            background: `linear-gradient(90deg, ${isDarkMode ? '#ff7a00' : '#212D82'} 0%, ${isDarkMode ? '#ff7a00' : '#212D82'} 40%, rgba(255, 122, 0, 0.8) 50%, ${isDarkMode ? '#ff7a00' : '#212D82'} 60%, ${isDarkMode ? '#ff7a00' : '#212D82'} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            lineHeight: '1.2',
            paddingBottom: '0.25rem'
          }}
        >
          SAT Practice Test Results
        </h1>

        {/* Overall Score Section */}
        <Card delay={0.1} index={0} className="mb-6 sm:mb-8 md:mb-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div className="flex flex-col items-center">
              <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-700 dark:text-gray-200 mb-2 sm:mb-3 md:mb-4 font-zonaProRegular">Total SAT Score</h2>
              <p className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-2" style={{ color: '#ff7a00' }}>
                {mappedData.overallScore} / {mappedData.maxScore}
              </p>
              <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm md:text-base lg:text-lg mb-3 sm:mb-4">Test Date: {mappedData.testDate}</p>

              <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6 w-full max-w-xl">
                <div className="flex-1 bg-blue-50 dark:bg-blue-900/20 p-3 sm:p-4 rounded-lg shadow-inner text-center">
                  <p className="text-xs sm:text-sm md:text-base lg:text-lg font-semibold text-blue-700 dark:text-blue-300">English Section Score</p>
                  <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-600 dark:text-blue-400">{mappedData.englishSection.score} / {mappedData.englishSection.maxScore}</p>
                </div>
                <div className="flex-1 bg-purple-50 dark:bg-purple-900/20 p-3 sm:p-4 rounded-lg shadow-inner text-center">
                  <p className="text-xs sm:text-sm md:text-base lg:text-lg font-semibold text-purple-700 dark:text-purple-300">Math Section Score</p>
                  <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-purple-600 dark:text-purple-400">{mappedData.mathSection.score} / {mappedData.mathSection.maxScore}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 content-start">
              <MetricCard
                title="Overall Accuracy"
                value={mappedData.overallAccuracyPercentage}
                unit="%"
                icon={CheckCircle}
                color="text-green-600 dark:text-green-400"
              />
              <MetricCard
                title="Total Time Taken"
                value={mappedData.totalTimeTakenDisplay}
                icon={Clock}
                color="text-indigo-600 dark:text-indigo-400"
              />

              <div className="col-span-2 w-full bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 p-3 sm:p-4 md:p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 relative">
                <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2 sm:mb-3 md:mb-4 font-zonaProRegular">Question Breakdown</h3>
                <div className="flex w-full h-5 sm:h-6 md:h-8 rounded-full overflow-hidden mb-2 sm:mb-3 md:mb-4 bg-gray-200 dark:bg-gray-700">
                  <div
                    className="bg-green-500 dark:bg-green-600 h-full rounded-l-full transition-all duration-1000 ease-out"
                    style={{ width: `${barWidths.correct}%` }}
                  />
                  <div
                    className="bg-red-500 dark:bg-red-600 h-full transition-all duration-1000 ease-out"
                    style={{ width: `${barWidths.incorrect}%` }}
                  />
                  <div
                    className="bg-gray-400 dark:bg-gray-600 h-full rounded-r-full transition-all duration-1000 ease-out"
                    style={{ width: `${barWidths.unanswered}%` }}
                  />
                </div>
                <div className="flex justify-around text-center font-bold text-sm sm:text-lg">
                  <div className="flex flex-col items-center">
                    <span className="text-green-600 dark:text-green-400">{mappedData.overallCorrect}</span>
                    <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">Correct</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-red-600 dark:text-red-400">{mappedData.overallIncorrect}</span>
                    <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">Incorrect</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-gray-600 dark:text-gray-400">{(topicTotalCount.english+topicTotalCount.math) - mappedData.overallCorrect - mappedData.overallIncorrect}</span>
                    <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">Unanswered</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
        {/* Section Overview: English and Math Side by Side Cards */}
        <section className="mb-10">
          {/* English Section Card */}
          <Card delay={0.2} index={1} className="mb-6">
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-700 dark:text-gray-200 mb-3 sm:mb-4 flex items-center font-zonaProUltra" style={{ color: isDarkMode ? '#ff7a00' : '#0a1a2f' }}><BookOpen size={24} className="mr-2 sm:mr-3 w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" style={{ color: '#a6a6a6' }} /> English Section</h2>
          <div className="flex flex-col xl:flex-row items-center w-full mb-4">
            {/* Score on the left */}
              <div className="flex flex-col items-center justify-center md:mr-8 min-w-[140px] sm:min-w-[180px] md:min-w-[220px] mb-3 sm:mb-4 md:mb-0">
              <p className="text-3xl sm:text-4xl md:text-5xl font-bold mb-1 sm:mb-2" style={{ color: '#ff7a00' }}>{mappedData.englishSection.score}</p>
              <span className="text-gray-500 dark:text-gray-400 text-sm sm:text-base md:text-lg">Score</span>
            </div>
            {/* Table on the right */}
            <div className="flex-1 flex flex-col gap-4 w-full">
              <div className="overflow-x-auto w-full max-w-full">
                <table className="w-full text-center rounded-2xl overflow-hidden border-separate border-spacing-0" style={{ height: 'auto' }}>
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700 text-black dark:text-gray-200 text-xs sm:text-sm md:text-base font-zonaProRegular font-bold">
                    <th className="py-2 px-1 sm:px-2 font-bold text-left"></th>
                    <th className="py-2 px-1 sm:px-2 font-bold">Correct</th>
                    <th className="py-2 px-1 sm:px-2 font-bold">Incorrect</th>
                    <th className="py-2 px-1 sm:px-2 font-bold">Unanswered</th>
                    <th className="py-2 px-1 sm:px-2 font-bold">Total</th>
                    <th className="py-2 px-1 sm:px-2 font-bold">Accuracy</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="font-bold">
                    <td className="py-2 px-1 sm:px-2 text-left text-xs sm:text-sm md:text-base text-gray-800 dark:text-gray-200">Overall English Performance</td>
                    <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-green-600 dark:text-green-400">{mappedData.englishSection.correct}</td>
                    <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-red-500 dark:text-red-400">{mappedData.englishSection.incorrect}</td>
                    <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-yellow-600 dark:text-yellow-400">{topicTotalCount.english - mappedData.englishSection.correct - mappedData.englishSection.incorrect}</td>
                    <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-gray-800 dark:text-gray-200">{topicTotalCount.english}</td>
                    <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-gray-800 dark:text-gray-200">{Math.round((mappedData.englishSection.correct / 54) * 100)}%</td>
                  </tr>
                </tbody>
              </table>
              </div>
              {mappedData.englishSection.modules.map((module, moduleIndex) => (
                <div key={moduleIndex} className="bg-blue-50 dark:bg-blue-900/20 p-4 h-full min-h-[220px] rounded-lg shadow-inner flex flex-col w-full max-w-full">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-blue-700 dark:text-blue-300">{module.name}</span>
                  </div>

                  {/* Replace BarChart with a table for English modules */}
                  <div className="overflow-x-auto w-full max-w-full">
                  <table className="w-full text-center rounded-2xl overflow-hidden border-separate border-spacing-0" style={{ height: 'auto' }}>
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-700 text-black dark:text-gray-200 text-xs sm:text-sm md:text-base font-zonaProRegular font-bold">
                        <th className="py-2 px-1 sm:px-2 font-bold text-left">Subtopic</th>
                        <th className="py-2 px-1 sm:px-2 font-bold">Correct</th>
                        <th className="py-2 px-1 sm:px-2 font-bold">Incorrect</th>
                        <th className="py-2 px-1 sm:px-2 font-bold">Unanswered</th>
                        <th className="py-2 px-1 sm:px-2 font-bold">Total</th>
                        <th className="py-2 px-1 sm:px-2 font-bold">Accuracy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {module.breakdown.map((item, idx) => (
                        <tr key={idx} className="border-b last:border-b-0 border-gray-200 dark:border-gray-600">
                          <td className="py-2 px-1 sm:px-2 text-left text-xs sm:text-sm md:text-base font-medium text-gray-800 dark:text-gray-200">{item.type}</td>
                          <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-green-600 dark:text-green-400 font-medium">{item.correct}</td>
                          <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-red-500 dark:text-red-400 font-medium">{item.incorrect}</td>
                          <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-yellow-600 dark:text-yellow-400 font-medium">{item.unanswered}</td>
                          <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base font-medium text-gray-800 dark:text-gray-200">{item.correct+item.incorrect+item.unanswered}</td>
                          <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base font-medium text-gray-800 dark:text-gray-200">{item.accuracy}%</td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-gray-300 dark:border-gray-600 font-bold">
                        <td className="py-2 px-1 sm:px-2 text-left text-xs sm:text-sm md:text-base text-gray-800 dark:text-gray-200">Total</td>
                        <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-green-600 dark:text-green-400">{module.correct}</td>
                        <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-red-500 dark:text-red-400">{module.incorrect}</td>
                        <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-yellow-600 dark:text-yellow-400">{module.totalQuestions-module.correct-module.incorrect}</td>
                        <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-gray-800 dark:text-gray-200">{module.totalQuestions}</td>
                        <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-gray-800 dark:text-gray-200">{module.correct+module.incorrect > 0 ? Math.round((module.correct / (27)) * 100) : 0}%</td>
                      </tr>
                    </tbody>
                  </table>
                  </div>
                </div>
                ))}
            </div>
          </div>
          </Card>
        </section>
        {/* Math Section Card */}
        <section className="mb-10">
        <Card delay={0.3} index={2}>
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-700 dark:text-gray-200 mb-3 sm:mb-4 flex items-center font-zonaProUltra" style={{ color: isDarkMode ? '#ff7a00' : '#0a1a2f' }}><Calculator size={24} className="mr-2 sm:mr-3 w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" style={{ color: '#a6a6a6' }} /> Math Section</h2>
          <div className="flex flex-col xl:flex-row items-center w-full mb-3 sm:mb-4">
            {/* Score on the left */}
                <div className="flex flex-col items-center justify-center md:mr-8 min-w-[140px] sm:min-w-[180px] md:min-w-[220px] mb-3 sm:mb-4 md:mb-0">
              <p className="text-3xl sm:text-4xl md:text-5xl font-bold mb-1 sm:mb-2" style={{ color: '#ff7a00' }}>{mappedData.mathSection.score}</p>
              <span className="text-gray-500 dark:text-gray-400 text-sm sm:text-base md:text-lg">Score</span>
            </div>
            {/* Table on the right */}
            <div className="flex-1 flex flex-col gap-4 w-full">
              <div className="overflow-x-auto w-full max-w-full">
                <table className="w-full text-center rounded-2xl overflow-hidden border-separate border-spacing-0" style={{ height: 'auto' }}>
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700 text-black dark:text-gray-200 text-xs sm:text-sm md:text-base font-zonaProRegular font-bold">
                      <th className="py-2 px-1 sm:px-2 font-bold text-left"></th>
                      <th className="py-2 px-1 sm:px-2 font-bold">Correct</th>
                      <th className="py-2 px-1 sm:px-2 font-bold">Incorrect</th>
                      <th className="py-2 px-1 sm:px-2 font-bold">Unanswered</th>
                      <th className="py-2 px-1 sm:px-2 font-bold">Total</th>
                      <th className="py-2 px-1 sm:px-2 font-bold">Accuracy</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="font-bold">
                      <td className="py-2 px-1 sm:px-2 text-left text-xs sm:text-sm md:text-base text-gray-800 dark:text-gray-200">Overall Math Performance</td>
                      <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-green-600 dark:text-green-400">{mappedData.mathSection.correct}</td>
                      <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-red-500 dark:text-red-400">{mappedData.mathSection.incorrect}</td>
                      <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-yellow-600 dark:text-yellow-400">{topicTotalCount.math - mappedData.mathSection.correct - mappedData.mathSection.incorrect}</td>
                      <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-gray-800 dark:text-gray-200">{topicTotalCount.math}</td>
                      <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-gray-800 dark:text-gray-200">{mappedData.mathSection.totalQuestions > 0 ? Math.round((mappedData.mathSection.correct / (44)) * 100) : 0}%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="flex-1 flex flex-col gap-4">
                {mappedData.mathSection.modules.map((module, moduleIndex) => (
                  <div key={moduleIndex} className="bg-purple-50 dark:bg-purple-900/20 p-4 h-full min-h-[220px] rounded-lg shadow-inner flex flex-col w-full max-w-full">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-purple-700 dark:text-purple-300">{module.name}</span>
                    </div>
                    {/* Module breakdown table */}
                    <div className="overflow-x-auto w-full max-w-full">
                      <table className="w-full text-center rounded-2xl overflow-hidden border-separate border-spacing-0" style={{ height: 'auto' }}>
                        <thead>
                          <tr className="bg-gray-50 dark:bg-gray-700 text-black dark:text-gray-200 text-xs sm:text-sm md:text-base font-zonaProRegular font-bold">
                            <th className="py-2 px-1 sm:px-2 font-bold text-left">Subtopic</th>
                            <th className="py-2 px-1 sm:px-2 font-bold">Correct</th>
                            <th className="py-2 px-1 sm:px-2 font-bold">Incorrect</th>
                            <th className="py-2 px-1 sm:px-2 font-bold">Unanswered</th>
                            <th className="py-2 px-1 sm:px-2 font-bold">Total</th>
                            <th className="py-2 px-1 sm:px-2 font-bold">Accuracy</th>
                          </tr>
                        </thead>
                        <tbody>
                          {module.breakdown.map((item, idx) => (
                            <tr key={idx} className="border-b last:border-b-0 border-gray-200 dark:border-gray-600">
                              <td className="py-2 px-1 sm:px-2 text-left text-xs sm:text-sm md:text-base font-medium text-gray-800 dark:text-gray-200">{item.subTopic || item.type}</td>
                              <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-green-600 dark:text-green-400 font-medium">{item.correct}</td>
                              <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-red-500 dark:text-red-400 font-medium">{item.incorrect}</td>
                              <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-yellow-600 dark:text-yellow-400 font-medium">{item.unanswered}</td>
                              <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base font-medium text-gray-800 dark:text-gray-200">{item.correct+item.incorrect+item.unanswered}</td>
                              <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base font-medium text-gray-800 dark:text-gray-200">{item.accuracy}%</td>
                            </tr>
                          ))}
                          <tr className="border-t-2 border-gray-300 dark:border-gray-600 font-bold">
                            <td className="py-2 px-1 sm:px-2 text-left text-xs sm:text-sm md:text-base text-gray-800 dark:text-gray-200">Total</td>
                            <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-green-600 dark:text-green-400">{module.correct}</td>
                            <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-red-500 dark:text-red-400">{module.incorrect}</td>
                            <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-yellow-600 dark:text-yellow-400">{module.totalQuestions - module.correct - module.incorrect}</td>
                            <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-gray-800 dark:text-gray-200">{module.totalQuestions}</td>
                            <td className="py-2 px-1 sm:px-2 text-xs sm:text-sm md:text-base text-gray-800 dark:text-gray-200">{module.totalQuestions > 0 ? Math.round((module.correct / (22)) * 100) : 0}%</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
        </section>
          </div>
        </div>

        {/* Question Review Section */}
        <div id="question-review-section" className="max-w-full mx-auto mt-8 sm:mt-12 md:mt-16">
          <Card delay={0.4} index={3} className="max-w-full mx-auto">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-zonaProUltra mb-3 sm:mb-4" style={{ color: isDarkMode ? '#ff7a00' : '#0a1a2f' }}>Questions Review</h2>
            <div className="mb-3 sm:mb-4 flex gap-1 sm:gap-2 overflow-x-auto">
              {QUESTION_TABS.map(tab => (
                <motion.button
                  key={tab.key}
                  className={`px-2 sm:px-4 py-1 sm:py-2 rounded-t-lg text-xs sm:text-sm md:text-base font-semibold border-b-2 transition-colors duration-150 whitespace-nowrap ${selectedTab === tab.key ? 'bg-white dark:bg-gray-800' : 'border-transparent text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                  style={selectedTab === tab.key ? { borderColor: '#34c759', color: '#34c759' } : {}}
                  onClick={() => setSelectedTab(tab.key)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {tab.label}
                </motion.button>
              ))}
            </div>
            <QuestionTable questions={questionsByTab[selectedTab]} loading={questionsLoading} error={questionsError} />
          </Card>
        </div>

        {/* Action Buttons Section */}
        <section id="action-buttons" className="mt-10 mb-10">
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
            <motion.button
              onClick={() => navigate('/practice')}
              className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-cyan-500 to-blue-500 dark:from-orange-500 dark:to-orange-600 text-white text-sm sm:text-base md:text-lg font-semibold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <BookOpen size={18} className="sm:w-5 sm:h-5" />
              Back to Practice
            </motion.button>
            <motion.button
              onClick={handleEmailReport}
              className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-green-500 to-green-600 dark:from-green-600 dark:to-green-700 text-white text-sm sm:text-base md:text-lg font-semibold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <BarChart3 size={18} className="sm:w-5 sm:h-5" />
              Email me this report (PDF)
            </motion.button>
            <motion.button
              id="download-pdf-button"
              onClick={generatePdf}
              className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-green-500 to-green-600 dark:from-green-600 dark:to-green-700 text-white text-sm sm:text-base md:text-lg font-semibold rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Download size={18} className="sm:w-5 sm:h-5" />
              Download Report (PDF)
            </motion.button>
          </div>
        </section>
      </div>

      {/* --- PDF Report Layout (Hidden from Screen) --- */}
      <div 
        id="pdf-layout-container" 
        style={{ 
          position: 'absolute', 
          top: '0', 
          left: '-9999px', 
          width: '100%', 
          zIndex: '-1000' 
        }}
      >
        <SATTestReportPDFLayout 
          mappedData={mappedData}
          questionsByTab={questionsByTab}
          topicTotalCount={topicTotalCount}
          barWidths={barWidths}
        />
      </div>
    </div>
  );
};

export default SATTestReportPage;
